
import { BlockT, ClampPoint, ClampTexture, ClampTextureHighLight, DYMODE_MODE, LineT, ModelUtils, MoveBlockCommand, MoveCommand, Point, STATUS_TYPE, TnEngineContext, TnEngineExtraContext, ViewEditor } from "pytha";
import { BufferGeometry, Float32BufferAttribute, MOUSE, Object3D, Points, PointsMaterial, Vector3 } from "three";
import { DeviceComponentT } from "../models/DeviceComponentT";
import { MonixMoveCommand } from "@/passets/pytha-engine/commands/MonixMoveCommand";

export class DeviceComponentClamp implements ClampPoint {

    uuid: string;

    entity: DeviceComponentT;
    attachPoint: Vector3;
    baseAttachPoint: Vector3;
    linkPoints: Vector3[];
    editor: ViewEditor;

    viewObj: Points;
    key: string;

    _status: number = STATUS_TYPE.NONE;
    _entityClone: DeviceComponentT;

    _activate = false;

    _vecTopLeft = new Vector3();
    _vecTopRight = new Vector3();
    _vecDownRight = new Vector3();
    _vecDownLeft = new Vector3();

    _size = 10;

    _helpLine: LineT;

    capturePoint: Vector3;
    captureEntityUuid: string;

    orthogonalPoint: Vector3;
    polarPoint: Vector3;

    context: TnEngineContext;
    extraContext: TnEngineExtraContext;

    point_move = new Point();

    rotateBasePoint: Point = null


    constructor(editor: ViewEditor, entity: DeviceComponentT, point: Vector3, key: string) {
        this.context = TnEngineContext.getInstance();
        this.extraContext = TnEngineExtraContext.getInstance();
        this.uuid = ModelUtils.generateUUID();
        this.editor = editor;
        this.entity = entity;
        this.attachPoint = point.clone();
        this.baseAttachPoint = point.clone()
        this.key = key;
        this.point_move.set(this.extraContext.mousePosContext.x, this.extraContext.mousePosContext.y, this.extraContext.mousePosContext.z);
        this.initHelpLine();
    }

    get activate(): boolean {
        return this._activate;
    }
    set activate(value: boolean) {
        if (this._status != STATUS_TYPE.NONE) return;
        this._activate = value;
        if (!!value) {
            this.editor.renderer.domElement.addEventListener('pointerdown', this.onPointerdown);
        } else {
            this.editor.renderer.domElement.removeEventListener('pointerdown', this.onPointerdown);
            this.editor.renderer.domElement.removeEventListener('pointermove', this.onPointerMove);
        }
    }

    initHelpLine = () => {
        this._helpLine = new LineT(this.attachPoint, this.attachPoint);
        this._helpLine.color.set('#969c9a');
        this._helpLine._isHelpLine = true;
    }

    set(entity, point) {
        this.entity = entity;
        this.attachPoint = point.clone();
        this.baseAttachPoint = point.clone()
    }

    onPointCapture = (point, uuid) => {
        this.capturePoint = point;
        this.captureEntityUuid = uuid;
    }

    onPointOrthogonal = (point) => {
        this.orthogonalPoint = point;
    }

    onPointPolarTracking = (point) => {
        this.polarPoint = point;
    }

    onPointerdown = (event) => {
        if (event.button !== MOUSE.LEFT) return;
        event.stopPropagation();
        if (this._status === STATUS_TYPE.NONE) {
            //add blockClone
            this._entityClone = this.entity.clone();
            this.extraContext.sceneContext.scene.add(this._entityClone.render());
            //add helpline
            this._helpLine.set(this.attachPoint, this.attachPoint);
            this._helpLine.updateViewGeometry();
            this.extraContext.sceneContext.scene.add(this._helpLine.render());

            this.editor.selectControl.setEnabled(false);

            //capture start
            this.editor.listeners.signals.needCapture.dispatch();
            this.editor.listeners.signals.onPointCapture.add(this.onPointCapture);
            //orthogonal start
            this.editor.listeners.signals.onPointOrthogonal.add(this.onPointOrthogonal);
            this.editor.listeners.signals.orthogonal.dispatch(this.attachPoint);
            // polarTracking start
            this.editor.listeners.signals.onPointPolarTracking.add(this.onPointPolarTracking);
            this.editor.listeners.signals.polarTracking.dispatch(this.attachPoint);
            this._status = STATUS_TYPE.CLAMP;
            this.editor.listeners.signals.clampControlDisabled.dispatch();
            this.editor.renderer.domElement.addEventListener('pointermove', this.onPointerMove);
            this.highLight();
            this.extraContext.dymodeContext.setMode(DYMODE_MODE.NONE);
        } else if (this._status === STATUS_TYPE.CLAMP) {
            if (this.capturePoint) {
                this.point_move.copy(this.capturePoint);
            } else if (this.orthogonalPoint) {
                this.point_move.copy(this.orthogonalPoint);
            } else if (this.polarPoint) {
                this.point_move.copy(this.polarPoint);
            }

            let dir = this.point_move.clone().sub(this.baseAttachPoint).normalize();
            let dist = this.point_move.clone().sub(this.baseAttachPoint).length();

            this.baseAttachPoint.copy(dir.clone().multiplyScalar(dist).add(this.baseAttachPoint))

            this.editor.history.execute(new MonixMoveCommand([this.entity], dir, dist, null));
            this.editor.listeners.signals.needRender.dispatch('redraw');

            this.extraContext.sceneContext.scene.remove(this._entityClone.viewObj);
            this.extraContext.sceneContext.scene.remove(this._helpLine.render());

            this.editor.selectControl.setEnabled(true);
            this.extraContext.dymodeContext.enabled = false;
            // this.editor.orthogonalControl.enabled = false;
            // this.editor.polarControl.enabled = false;
            this.editor.listeners.signals.clampControlEnabled.dispatch();
            this.editor.listeners.signals.onClampNeedUpdate.dispatch(this.entity);

            this.editor.listeners.signals.noNeedCapture.dispatch();
            this.editor.listeners.signals.onPointCapture.remove(this.onPointCapture);

            this.editor.listeners.signals.onPointOrthogonal.remove(this.onPointOrthogonal);
            this.editor.listeners.signals.onPointPolarTracking.remove(this.onPointPolarTracking);

            this._status = STATUS_TYPE.NONE;
            this.editor.listeners.signals.needRender.dispatch('redraw');
            this.editor.renderer.domElement.removeEventListener('pointermove', this.onPointerMove);
            this.unHighLight();
            this.activate = false;
            this.extraContext.dymodeContext.setMode(DYMODE_MODE.COMMAND);
        }
    }

    onPointerMove = (event) => {
        this.point_move.set(this.extraContext.mousePosContext.x, this.extraContext.mousePosContext.y, this.extraContext.mousePosContext.z);
        if (this.capturePoint) {
            this.point_move.copy(this.capturePoint);
        } else if (this.orthogonalPoint) {
            this.point_move.copy(this.orthogonalPoint);
        } else if (this.polarPoint) {
            this.point_move.copy(this.polarPoint);
        }
        this._helpLine.setValue('end', this.point_move);
        this._helpLine.updateViewGeometry();
        let dir = this.point_move.clone().sub(this.attachPoint).normalize();
        let dist = this.point_move.clone().sub(this.attachPoint).length();

        let point = dir.clone().multiplyScalar(dist).add(this.attachPoint);
        this.attachPoint.copy(point)

        this._entityClone.move(dir, dist);

        this._entityClone.updateViewGeometry();
    }

    updateGeometry = (value: Vector3) => {
        this.attachPoint.copy(value);
        this.baseAttachPoint.copy(value)
        let point = this.viewObj;
        if (!point) return;
        point.geometry.setAttribute('position', new Float32BufferAttribute([this.attachPoint.x, this.attachPoint.y, this.attachPoint.z], 3));
        point.geometry.computeBoundingSphere();
    }

    highLight = () => {
        let point = this.viewObj;
        if (!point) return;
        let mat = <PointsMaterial>point.material;
        mat.map = ClampTextureHighLight;
    }

    unHighLight = () => {
        let point = this.viewObj;
        if (!point) return;
        let mat = <PointsMaterial>point.material;
        mat.map = ClampTexture;
    }

    render(): Object3D {

        if (this.viewObj) return this.viewObj;

        const texture = ClampTexture;

        const geo = new BufferGeometry();
        geo.setAttribute('position', new Float32BufferAttribute([this.attachPoint.x, this.attachPoint.y, this.attachPoint.z], 3));

        const point = new Points(geo, new PointsMaterial({ map: texture, size: this._size }))

        this.viewObj = point;
        return point;
    }

    dispose() {
        this._status = STATUS_TYPE.NONE;
        this.activate = false;
        this.editor.renderer.domElement.removeEventListener('pointermove', this.onPointerMove);
        this.editor.listeners.signals.onPointCapture.remove(this.onPointCapture);
        this.editor.listeners.signals.onPointOrthogonal.remove(this.onPointOrthogonal);
        this.editor.listeners.signals.onPointPolarTracking.remove(this.onPointPolarTracking);
        this.editor.selectControl.setEnabled(true);
        // this.editor.orthogonalControl.enabled = false;
        // this.editor.polarControl.enabled = false;
        if (this.viewObj && this.viewObj.parent) {
            this.viewObj.parent.remove(this.viewObj);
        }
        if (this._entityClone && this._entityClone.viewObj && this._entityClone.viewObj.parent) {
            this._entityClone.viewObj.parent?.remove(this._entityClone.viewObj);
        }
        if (this._helpLine && this._helpLine.viewObj && this._helpLine.viewObj.parent) {
            this._helpLine.viewObj.parent?.remove(this._helpLine.viewObj);
        }
        this.viewObj = null;
    }
}